import { readAccountInstant } from "../../../../utils/date/read-account-instant.ts"
import { type Lead } from "../../types/lead.types.ts"

type LeadActivityKind =
  | "created"
  | "event"
  | "attachment"
  | "proposal"
  | "contract"

interface LeadActivityEntry {
  id: string
  kind: LeadActivityKind
  labelKey: string
  labelValues: Record<string, string>
  at: string | null
  /** `at` read as an instant: proposals, contracts and files arrive rendered
   * in the account's format and zone, the lead and its events as ISO. */
  moment: number | null
  actor: string | null
}

interface ActivityAccount {
  dateFormat?: string | null
  timezone?: string | null
}

const LEAD_RECENT_ACTIVITY_LIMIT = 5


/**
 * Assembled from the lead payload alone — the workspace issues no extra request
 * for the Overview card, and the Activity tab owns the full history.
 */
function leadRecentActivity(
  lead: Lead | null,
  limit: number = LEAD_RECENT_ACTIVITY_LIMIT,
  account: ActivityAccount = {}
): LeadActivityEntry[] {
  const entries: Omit<LeadActivityEntry, "moment">[] = []

  lead?.proposals?.forEach((proposal) =>
    entries.push({
      id: `proposal-${proposal.id}`,
      kind: "proposal",
      labelKey: "leadWorkspace.cards.activity.entries.proposal",
      labelValues: { version: proposal.version_display ?? "" },
      at: proposal.updated || proposal.created || null,
      actor: proposal.created_by_name || null
    })
  )

  lead?.contracts?.forEach((contract) =>
    entries.push({
      id: `contract-${contract.id}`,
      kind: "contract",
      labelKey: "leadWorkspace.cards.activity.entries.contract",
      labelValues: { version: contract.version_display ?? "" },
      at: contract.updated || contract.created || null,
      actor: contract.created_by_name || null
    })
  )

  lead?.attachments?.forEach((attachment) =>
    entries.push({
      id: `attachment-${attachment.id}`,
      kind: "attachment",
      labelKey: "leadWorkspace.cards.activity.entries.attachment",
      labelValues: { name: attachment.file_name ?? "" },
      at: attachment.created || null,
      actor: null
    })
  )

  lead?.lead_calendar_events?.forEach((event) =>
    entries.push({
      id: `event-${event.id}`,
      kind: "event",
      labelKey: "leadWorkspace.cards.activity.entries.event",
      labelValues: { title: event.title ?? "" },
      at: event.start_at || event.created || null,
      actor: event.created_by?.full_name || null
    })
  )

  if (lead?.created) {
    entries.push({
      id: `created-${lead.id}`,
      kind: "created",
      labelKey: "leadWorkspace.cards.activity.entries.created",
      labelValues: {},
      at: lead.created,
      actor: lead.created_by_name || null
    })
  }

  // A dateless entry cannot be placed on the timeline, so it sinks to the end
  // rather than displacing something that can be dated.
  return entries
    .map((entry) => ({
      ...entry,
      moment:
        readAccountInstant(entry.at, account.dateFormat, account.timezone)?.valueOf() ??
        null
    }))
    .sort((a, b) => (b.moment ?? -Infinity) - (a.moment ?? -Infinity))
    .slice(0, limit)
}

export {
  type ActivityAccount,
  type LeadActivityKind,
  type LeadActivityEntry,
  LEAD_RECENT_ACTIVITY_LIMIT,
  leadRecentActivity
}
